import { DateTime } from "luxon";

/**
 * The care team's business hours.
 *
 * `zone` is an IANA name rather than a fixed offset because the copy says
 * "Eastern Time", which is EDT for most of the year — a hardcoded -05:00 would
 * be an hour wrong from March to November.
 *
 * When changing these, update `patientChat.outsideHoursNotice` in every locale
 * under `packages/i18n/messages`: the hours are spelled out to the patient
 * there and will otherwise disagree with this check.
 */
export const BUSINESS_HOURS = {
  zone: "America/New_York",
  /** Inclusive. 07:00 is open. */
  startHour: 7,
  /** Exclusive. 19:00 is closed. */
  endHour: 19,
  /** Luxon weekdays: 1 = Monday, 7 = Sunday. */
  firstWeekday: 1,
  lastWeekday: 5,
} as const;

/**
 * The instant is converted into the care team's zone before anything is read
 * off it, so a patient's own device timezone never changes the answer.
 *
 * `now` is injectable because `packages/ui`, where the banner lives, has no
 * test infrastructure — this is the only place the rule can be tested.
 */
export const isOutsideBusinessHours = (
  now: DateTime = DateTime.now()
): boolean => {
  const local = now.setZone(BUSINESS_HOURS.zone);

  const isWeekend =
    local.weekday < BUSINESS_HOURS.firstWeekday ||
    local.weekday > BUSINESS_HOURS.lastWeekday;

  const isOffHours =
    local.hour < BUSINESS_HOURS.startHour ||
    local.hour >= BUSINESS_HOURS.endHour;

  return isWeekend || isOffHours;
};
